import type { RouteObject } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import { HelpAndSupport, TicketsList, UpsertTicket, TicketDetailsView } from "@/utils/lazyLoadComponents";
import { store } from "@/store";
import { helpAndSupportApi } from "@/services/(company)/api.help-and-support";

// ─── Path Segments ────────────────────────────────────────────────────────────
export const COMPANY_HELP_SUPPORT_DYNAMIC_ID_KEY = "id";

export enum COMPANY_HELP_SUPPORT_PATH_SEGMENTS {
  ROOT = "support-tickets",
  LIST = "tickets",
  NEW = "new",
  DETAILS = `:${COMPANY_HELP_SUPPORT_DYNAMIC_ID_KEY}`,
  VIEW = "view",
}

// ─── Route Definition ─────────────────────────────────────────────────────────
export const companyHelpSupportRoutes: RouteObject = {
  path: COMPANY_HELP_SUPPORT_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Help & Support" },
  HydrateFallback: Spinner,
  children: [
    { index: true, Component: HelpAndSupport },
    {
      path: COMPANY_HELP_SUPPORT_PATH_SEGMENTS.LIST,
      handle: { breadcrumb: "Tickets" },
      Component: TicketsList,
    },
    {
      path: COMPANY_HELP_SUPPORT_PATH_SEGMENTS.NEW,
      handle: { breadcrumb: "Create New Ticket" },
      Component: UpsertTicket,
    },
    {
      path: COMPANY_HELP_SUPPORT_PATH_SEGMENTS.DETAILS,
      loader: async ({ params }) => {
        const id = params[COMPANY_HELP_SUPPORT_DYNAMIC_ID_KEY]!;
        const result = await store.dispatch(helpAndSupportApi.endpoints.getTicketById.initiate(id));
        const name = result.data?.data?.subject ?? `Ticket #${id}`;
        return { breadcrumbLabel: name };
      },
      handle: {
        breadcrumb: (data: { breadcrumbLabel: string }) => data?.breadcrumbLabel ?? "Ticket",
      },
      children: [
        {
          path: COMPANY_HELP_SUPPORT_PATH_SEGMENTS.VIEW,
          handle: { breadcrumb: "View" },
          Component: TicketDetailsView,
        },
      ],
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const COMPANY_HELP_SUPPORT_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_HELP_SUPPORT_PATH_SEGMENTS.ROOT)),
  LIST: withCompanyPrefix(addSlashes(COMPANY_HELP_SUPPORT_PATH_SEGMENTS.ROOT, COMPANY_HELP_SUPPORT_PATH_SEGMENTS.LIST)),
  NEW: withCompanyPrefix(addSlashes(COMPANY_HELP_SUPPORT_PATH_SEGMENTS.ROOT, COMPANY_HELP_SUPPORT_PATH_SEGMENTS.NEW)),
  VIEW: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_HELP_SUPPORT_PATH_SEGMENTS.ROOT, String(id), COMPANY_HELP_SUPPORT_PATH_SEGMENTS.VIEW),
    ),
});
